/* eslint-disable */
import {
  always,
  applySpec,
  compose,
  converge,
  equals,
  identity,
  ifElse,
  includes,
  join,
  map,
  mergeLeft,
  prop,
  propOr,
  when,
  evolve,
  T,
  isNil,
  __,
} from 'ramda';
import axios from 'axios';
import { async, collection, string } from '@555platform/utility-belt.js';
import { isRootUserId, resolver } from '../util';
import { crypto } from '../../util';
import { queryApplications, queryapnKeyCert, queryapnAuthToken, queryfgcm } from '../../models';
import config from '../../config';

// Helpers
const SENSITIVE_FIELDS = ['clientSecret', 'privateKey'];

const isSensitiveField = compose(
  includes(__, SENSITIVE_FIELDS),
  join('.'),
);

const redactSensitiveField = ifElse((_, path) => isSensitiveField(path), string.redact, identity);

const redactSensitiveFields = collection.project(redactSensitiveField);

const redactWrappedSensitiveFields = evolve({
  fields: {
    clientSecret: string.redact,
    privateKey: string.redact,
  },
});

const unwrapApplication = propOr({}, 'fields');

const getUserId = compose(
  prop('user_id'),
  resolver.ctxProp('user'),
);

const formatResponse = compose(
  redactSensitiveFields,
  unwrapApplication,
);

const formatWrappedResponse = compose(redactWrappedSensitiveFields);

const relaxConstraint = applySpec({ $regex: string.createTemplate('.*${}.*') });

const relaxConstraints = collection.project(relaxConstraint);

const getUserIdFromContext = compose(
  when(isRootUserId, always(undefined)),
  prop('user_id'),
  resolver.ctxProp('user'),
);

const getConstraints = compose(
  relaxConstraints,
  resolver.argProp('constraints'),
);

const queryById = applySpec({ applicationID: resolver.argProp('applicationID') });

const buildCredentialRotationQuery = async.converge(mergeLeft, [
  queryById,
  crypto.generateCredentials,
]);

const buildKeyRotationQuery = async.converge(mergeLeft, [queryById, crypto.generateKeyPair]);

const domainRegex = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;

const checkDomain = name => domainRegex.test(name);

const testDomainName = ifElse(isNil, T, checkDomain);

const validateDomainName = ifElse(
  compose(
    testDomainName,
    prop(['domain']),
  ),
  identity,
  (...args) => {
    throw new Error('Invalid domain name');
  },
);

// Resolvers

const applicationById = converge(queryApplications('findApplicationByAppID'), [
  resolver.argProp('applicationID'),
]);

const findApplication = resolver.compose(
  formatResponse,
  applicationById,
);

const findApplicationOwner = resolver.compose(applicationById);

const findApplicationClientID = resolver.compose(
  prop('clientID'),
  unwrapApplication,
  applicationById,
);

const findApplicationClientSecret = resolver.compose(
  prop('clientSecret'),
  unwrapApplication,
  applicationById,
);

const findApplicationPrivateKey = resolver.compose(
  prop('privateKey'),
  unwrapApplication,
  applicationById,
);

const findApplications = resolver.compose(
  map(formatResponse),
  converge(queryApplications('search'), [
    getConstraints,
    resolver.argProp('filter'),
    getUserIdFromContext,
    resolver.argProp('skip'),
    resolver.argProp('limit'),
    ifElse(
      compose(
        equals('personal'),
        resolver.argProp('owner'),
      ),
      getUserIdFromContext,
      resolver.argProp('owner'),
    ),
  ]),
);

const findAllApplications = resolver.compose(
  evolve({ applications: map(formatWrappedResponse) }),
  converge(queryApplications('findApplications'), [
    resolver.argProp('filter'),
    resolver.argProp('skip'),
    resolver.argProp('limit'),
    ifElse(
      compose(
        equals('personal'),
        resolver.argProp('owner'),
      ),
      getUserIdFromContext,
      resolver.argProp('owner'),
    ),
  ]),
);

const upsertApplication = resolver.compose(
  formatResponse,
  converge(queryApplications('upsert'), [
    compose(
      validateDomainName,
      resolver.argProp('application'),
    ),
    ifElse(
      compose(
        equals('personal'),
        resolver.argProp('currentTeam'),
      ),
      getUserIdFromContext,
      resolver.argProp('currentTeam'),
    ),
  ]),
);

const rotateCredentials = resolver.compose(
  formatResponse,
  async.converge(queryApplications('upsert'), [buildCredentialRotationQuery, getUserIdFromContext]),
);

const rotateKeys = resolver.compose(
  formatResponse,
  async.converge(queryApplications('upsert'), [buildKeyRotationQuery, getUserIdFromContext]),
);

const changeApplicationOwner = converge(queryApplications('changeApplicationOwner'), [
  resolver.argProp('id'),
  resolver.argProp('newOwnerID'),
]);

const getAllCount = resolver.compose(
  converge(queryApplications('findAllApplicationsCount'), [
    resolver.argProp('filter'),
    resolver.argProp('owner'),
  ]),
);

const getUserAppCount = resolver.compose(
  converge(queryApplications('findUserApplicationCount'), [
    resolver.argProp('filter'),
    getUserIdFromContext,
  ]),
);

const findApnKeyCert = resolver.compose(
  unwrapApplication,
  converge(queryapnKeyCert('findByAppKey'), [resolver.argProp('appKey'), getUserId]),
);

const findApnTokenAuth = resolver.compose(
  unwrapApplication,
  converge(queryapnAuthToken('findByAppKey'), [resolver.argProp('appKey'), getUserId]),
);

const findFgcm = resolver.compose(
  unwrapApplication,
  converge(queryfgcm('findByAppKey'), [resolver.argProp('appKey'), getUserId]),
);

const APNWithKeyCert = resolver.authenticatedResolver.createResolver(
  async (root, { apnKeyCert }, { user }) =>
    axios({
      method: 'put',
      url: `${config.ntmServiceUrl}v1/appdomain`,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.access_token}`,
      },
      data: {
        app_domain_client_id: apnKeyCert.app_domain_client_id,
        app_domain: apnKeyCert.domain,
        apns_cert: apnKeyCert.apns_cert,
        apns_key: apnKeyCert.apns_key,
        apns_production: apnKeyCert.apns_production,
      },
    })
      .then(response => true)
      .catch(error => {
        if (error.response) {
          if (error.response && error.response.status === 401) {
            // token expired - logout
            throw Error('Unauthorized');
          } else {
            throw Error('Could not update settings');
          }
        } else if (error.request) {
          throw Error('Could not contact server');
        } else {
          throw Error('Something did not work');
        }
      }),
);

const APNWithAuthToken = resolver.authenticatedResolver.createResolver(
  async (root, { apnAuthToken }, { user }) =>
    axios({
      method: 'put',
      url: `${config.ntmServiceUrl}v1/appdomain`,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.access_token}`,
      },
      data: {
        app_domain_client_id: apnAuthToken.app_domain_client_id,
        app_domain: apnAuthToken.domain,
        apns_team_id: apnAuthToken.apns_team_id,
        apns_key_id: apnAuthToken.apns_key_id,
        apns_priv_key: apnAuthToken.apns_priv_key,
        apns_production: apnAuthToken.apns_production,
      },
    })
      .then(response => true)
      .catch(error => {
        if (error.response) {
          if (error.response && error.response.status === 401) {
            // token expired - logout
            throw Error('Unauthorized');
          } else {
            throw Error('Could not update settings');
          }
        } else if (error.request) {
          throw Error('Could not contact server');
        } else {
          throw Error('Something did not work');
        }
      }),
);

const Fgcm = resolver.authenticatedResolver.createResolver(async (root, { updateFgcm }, { user }) =>
  axios({
    method: 'put',
    url: `${config.ntmServiceUrl}v1/appdomain`,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${user.access_token}`,
    },
    data: {
      app_domain_client_id: updateFgcm.app_domain_client_id,
      app_domain: updateFgcm.domain,
      gcm_api_key: updateFgcm.gcm_api_key,
    },
  })
    .then(response => true)
    .catch(error => {
      if (error.response) {
        if (error.response && error.response.status === 401) {
          // token expired - logout
          throw Error('Unauthorized');
        } else {
          throw Error('Could not update settings');
        }
      } else if (error.request) {
        throw Error('Could not contact server');
      } else {
        throw Error('Something did not work');
      }
    }),
);

export default {
  Query: {
    application: findApplication,
    applications: findApplications,
    applicationOwner: findApplicationOwner,
    allApplications: findAllApplications,
    applicationClientID: findApplicationClientID,
    applicationClientSecret: findApplicationClientSecret,
    applicationPrivateKey: findApplicationPrivateKey,
    allApplicationCount: getAllCount,
    userApplicationCount: getUserAppCount,
    apnKeyCert: findApnKeyCert,
    apnAuthToken: findApnTokenAuth,
    fgcm: findFgcm,
  },
  Mutation: {
    application: upsertApplication,
    apnKeyCert: APNWithKeyCert,
    apnAuthToken: APNWithAuthToken,
    updateFgcm: Fgcm,
    rotateApplicationCredentials: rotateCredentials,
    rotateApplicationKeys: rotateKeys,
    changeAppOwner: changeApplicationOwner,
  },
};
